import React from 'react';
import { motion } from 'framer-motion';
import { ShoppingCart, Star, Filter, Search, ArrowRight, ShieldCheck, Truck, RotateCcw } from 'lucide-react';
import { useCartStore } from '../../store/cartStore';

const products = [
  {
    id: 'j1',
    name: 'Aries Fire Talisman',
    price: 4500,
    image: 'https://cdn.pixabay.com/photo/2015/12/14/14/09/jewellery-1092562_1280.jpg',
    category: 'Necklace',
    sign: 'Aries',
    material: '18K Gold Plated'
  },
  {
    id: 'j2',
    name: 'Taurus Earth Ring',
    price: 3200,
    image: 'https://cdn.pixabay.com/photo/2022/08/16/04/52/jewel-7389356_1280.jpg',
    category: 'Ring',
    sign: 'Taurus',
    material: 'Sterling Silver'
  },
  {
    id: 'j3',
    name: 'Gemini Air Bracelet',
    price: 2800,
    image: 'https://cdn.pixabay.com/photo/2016/03/31/22/48/bracelet-1297219_1280.png',
    category: 'Bracelet',
    sign: 'Gemini',
    material: 'Rose Gold'
  },
  {
    id: 'j4',
    name: 'Cancer Moon Pendant',
    price: 5200,
    image: 'https://cdn.pixabay.com/photo/2014/07/18/13/49/jewels-396441_1280.jpg',
    category: 'Necklace',
    sign: 'Cancer',
    material: 'White Gold'
  },
  {
    id: 'j5',
    name: 'Leo Sun Medallion',
    price: 6500,
    image: 'https://cdn.pixabay.com/photo/2015/12/13/05/45/jewels-1090711_1280.jpg',
    category: 'Necklace',
    sign: 'Leo',
    material: '24K Gold'
  },
  {
    id: 'j6',
    name: 'Virgo Earth Earring',
    price: 2400,
    image: 'https://cdn.pixabay.com/photo/2015/01/25/19/39/jewelry-611867_1280.jpg',
    category: 'Earring',
    sign: 'Virgo',
    material: 'Sterling Silver'
  }
];

const Shop: React.FC = () => {
  const { addItem } = useCartStore();
  const [filter, setFilter] = React.useState('All');

  const filteredProducts = filter === 'All' 
    ? products 
    : products.filter(p => p.category === filter);

  return (
    <div className="min-h-screen bg-midnight py-24 noise-overlay">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="mb-20 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="text-gold text-[10px] font-bold uppercase tracking-[0.3em] mb-4 block">Celestial Collection</span>
            <h1 className="text-5xl md:text-7xl font-serif font-bold mb-8">Zodiac Jewelry</h1>
            <p className="text-gray-400 max-w-2xl mx-auto font-light leading-relaxed">
              Hand-crafted celestial talismans designed to align your energy with the stars. Each piece is ethically sourced and blessed by our master astrologers.
            </p>
          </motion.div>
        </div>

        {/* Filters & Search */}
        <div className="flex flex-col md:flex-row justify-between items-center mb-16 gap-8">
          <div className="flex items-center space-x-4 overflow-x-auto pb-4 w-full md:w-auto no-scrollbar">
            {['All', 'Necklace', 'Ring', 'Bracelet', 'Earring'].map((cat) => (
              <button
                key={cat}
                onClick={() => setFilter(cat)}
                className={`px-8 py-3 rounded-full text-[10px] font-bold uppercase tracking-[0.2em] transition-all duration-300 whitespace-nowrap border ${
                  filter === cat 
                    ? 'bg-gold border-gold text-midnight shadow-lg shadow-gold/20' 
                    : 'bg-white/5 border-white/10 text-gray-500 hover:border-gold/30 hover:text-gold'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
          <div className="relative w-full md:w-96">
            <Search className="absolute left-6 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600" />
            <input 
              type="text" 
              placeholder="Search collection..." 
              className="w-full pl-14 pr-6 py-4 bg-white/5 border border-white/10 rounded-full focus:outline-none focus:border-gold transition-all text-sm text-white placeholder:text-gray-600"
            />
          </div>
        </div>

        {/* Product Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10 mb-32">
          {filteredProducts.map((product, idx) => (
            <motion.div
              key={product.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1 }}
              className="group glass-card overflow-hidden"
            >
              <div className="aspect-square relative overflow-hidden">
                <img 
                  src={product.image} 
                  alt={product.name} 
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000"
                />
                <div className="absolute top-6 right-6">
                  <span className="px-4 py-1.5 bg-midnight/80 backdrop-blur-xl text-gold text-[9px] font-black uppercase tracking-[0.2em] rounded-full border border-gold/20 shadow-2xl">
                    {product.sign}
                  </span>
                </div>
                <div className="absolute inset-0 bg-midnight/40 opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-center justify-center">
                  <button
                    onClick={() => addItem({ ...product, quantity: 1, type: 'product' })}
                    className="px-8 py-4 bg-white text-midnight font-bold rounded-full hover:bg-gold transition-all duration-300 transform translate-y-4 group-hover:translate-y-0 flex items-center"
                  >
                    <ShoppingCart className="w-4 h-4 mr-2" />
                    Quick Add
                  </button>
                </div>
              </div>
              <div className="p-10">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-2xl font-serif font-bold text-white group-hover:text-gold transition-colors duration-300">{product.name}</h3>
                    <p className="text-gray-600 text-[10px] uppercase tracking-widest mt-1">{product.material}</p>
                  </div>
                  <span className="text-xl text-gold font-serif font-bold">₹{product.price.toLocaleString('en-IN')}</span>
                </div>
                <div className="flex items-center space-x-1 mb-8">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-3 h-3 text-gold fill-gold" />)}
                  <span className="text-[10px] text-gray-600 ml-2">(48 reviews)</span>
                </div>
                <button
                  onClick={() => addItem({ ...product, quantity: 1, type: 'product' })}
                  className="w-full py-4 bg-white/5 border border-white/10 text-white text-[10px] font-bold uppercase tracking-[0.2em] rounded-xl hover:bg-gold hover:text-midnight hover:border-gold transition-all duration-300 flex items-center justify-center group/btn"
                >
                  Add to Cart
                  <ArrowRight className="ml-2 w-4 h-4 group-hover/btn:translate-x-2 transition-transform" />
                </button>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Trust Section */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 py-20 border-t border-white/5">
          {[
            { icon: <Truck className="w-6 h-6 text-gold" />, title: "Free Express Shipping", desc: "Complimentary insured delivery on all orders above ₹5,000." },
            { icon: <ShieldCheck className="w-6 h-6 text-gold" />, title: "Authenticity Guaranteed", desc: "Every piece comes with a certificate of authenticity and ritual blessing." },
            { icon: <RotateCcw className="w-6 h-6 text-gold" />, title: "14-Day Returns", desc: "Not aligned? Return your purchase within 14 days for a full refund." }
          ].map((item, idx) => (
            <div key={idx} className="text-center space-y-4">
              <div className="w-12 h-12 rounded-full bg-gold/5 border border-gold/10 flex items-center justify-center mx-auto mb-6">
                {item.icon}
              </div>
              <h4 className="text-white font-serif font-bold text-lg">{item.title}</h4>
              <p className="text-gray-500 text-sm font-light leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Shop;
